import { Alert, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Caption, ThemedText, Body } from '@/components/ui/Text';
import { VoteButton } from '@/components/collaboration/VoteButton';
import type { MaybeItem, MaybeItemTag } from '@/services/maybeItems';

const TAG_LABELS: Record<MaybeItemTag, string> = {
  indoor: 'Indoor',
  'quick-stop': 'Quick stop',
  'high-energy': 'High energy',
  quiet: 'Quiet',
};

type MaybeItemCardProps = {
  tripId: string;
  item: MaybeItem;
  canEdit?: boolean;
  onSchedule?: () => void;
  onArchive?: () => void;
  /** Display label for who suggested this idea (e.g. "Alex", "You", "Trip owner") */
  suggestedByLabel?: string;
  isSaving?: boolean;
  isScheduling?: boolean;
  isArchiving?: boolean;
};

export function MaybeItemCard({
  tripId,
  item,
  canEdit = false,
  onSchedule,
  onArchive,
  suggestedByLabel,
  isSaving = false,
  isScheduling = false,
  isArchiving = false,
}: MaybeItemCardProps) {
  const theme = useTheme();

  const confirmArchive = () => {
    Alert.alert('Archive idea?', 'This will hide it from the Maybe list.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Archive', style: 'destructive', onPress: () => onArchive?.() },
    ]);
  };

  const isOptimistic = item.id.startsWith('__optimistic_');
  const disableSchedule = isScheduling || isArchiving;
  const disableArchive = isSaving || isScheduling || isArchiving;

  return (
    <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
      <View style={{ gap: theme.spacing(1) }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 15, fontWeight: '800' }}>
              {item.title}
            </ThemedText>
            {!!item.description?.trim() && (
              <Body color={theme.colors.textSecondary} style={{ fontSize: 12, lineHeight: 18 }}>
                {item.description.trim()}
              </Body>
            )}
            {suggestedByLabel != null && suggestedByLabel !== '' && (
              <Caption style={{ fontSize: 11, marginTop: theme.spacing(0.25), color: theme.colors.textSecondary }}>
                Suggested by <ThemedText variant="bodyStrong" style={{ fontSize: 11, color: theme.colors.primary }}>{suggestedByLabel}</ThemedText>
              </Caption>
            )}
          </View>

          {!isOptimistic && (
            <VoteButton
              tripId={tripId}
              itemId={item.id}
              itemType="suggestion"
              size="small"
              showCounts={true}
              orientation="horizontal"
            />
          )}
        </View>

        {isSaving && (
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            Saving…
          </Caption>
        )}

        {Array.isArray(item.tags) && item.tags.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
            {item.tags.slice(0, 6).map((t) => (
              <View
                key={t}
                style={{
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.35),
                  borderRadius: theme.radii.pill,
                  backgroundColor: theme.colors.borderMuted + '25',
                }}
              >
                <Caption style={{ fontSize: 11, fontWeight: '700', color: theme.colors.textSecondary }}>
                  {TAG_LABELS[t] || t}
                </Caption>
              </View>
            ))}
          </View>
        )}

        {canEdit && (
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button
              label={isScheduling ? 'Scheduling…' : 'Schedule'}
              variant="secondary"
              onPress={onSchedule}
              style={{ flex: 1 }}
              disabled={disableSchedule}
              icon={<Ionicons name="calendar-outline" size={16} color={theme.colors.primary} />}
            />
            <Pressable
              onPress={confirmArchive}
              style={({ pressed }) => ({
                minHeight: 44,
                paddingHorizontal: theme.spacing(1.25),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.borderMuted + '25',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed || disableArchive ? 0.7 : 1,
              })}
              accessibilityRole="button"
              accessibilityLabel="Archive idea"
              disabled={disableArchive}
            >
              <Ionicons name="archive-outline" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          </View>
        )}
      </View>
    </Card>
  );
}

